/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

/**
 * React Query hooks for comments system
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type {
  Comment,
  CommentWithContext,
  CommentableType,
  CreateCommentInput,
  UpdateCommentInput,
} from '@/types/database'
import { toast } from 'sonner'
import { useEffect } from 'react'

// =============================================
// QUERY KEYS
// =============================================

export const commentKeys = {
  all: ['comments'] as const,
  lists: () => [...commentKeys.all, 'list'] as const,
  list: (type: CommentableType, id: string) =>
    [...commentKeys.lists(), type, id] as const,
  projectActivity: (projectId: string) =>
    [...commentKeys.all, 'activity', projectId] as const,
}

// =============================================
// HELPER FUNCTIONS
// =============================================

// Helper function kept for future use - currently unused but may be needed
// /**
//  * Fetch user details for a comment
//  * For now, just return minimal user info - the full name will come from useAuth context
//  * or we can enhance this later with a user profile lookup
//  */
// async function fetchUserDetails(userId: string | null) {
//   if (!userId) return null
//
//   // Try to get user profile
//   const { data, error } = await supabase
//     .from('profiles')
//     .select('id, email, full_name, avatar_url')
//     .eq('id', userId)
//     .maybeSingle()
//
//   if (error) {
//     console.error('[fetchUserDetails] Error fetching profile:', error)
//   }
//
//   if (data) {
//     return {
//       id: data.id,
//       name: data.full_name || data.email || 'Unknown User',
//       email: data.email,
//       avatar_url: data.avatar_url,
//     }
//   }
//
//   // Fallback: return minimal info
//   return {
//     id: userId,
//     name: 'Unknown User',
//     email: undefined,
//   }
// }

/**
 * Parse @mentions from comment content
 * Supports both @username and @[User Name] formats
 */
export function parseCommentMentions(content: string): string[] {
  const mentionRegex = /@\[([^\]]+)\]|@(\w+)/g
  const mentions: string[] = []
  let match

  while ((match = mentionRegex.exec(content)) !== null) {
    mentions.push(match[1] || match[2])
  }

  return mentions
}

/**
 * Render comment content with highlighted @mentions
 */
export function renderCommentWithMentions(
  content: string,
  mentions?: Array<{ mentioned_user_id: string; mentioned_user?: { name?: string } }>
): string {
  if (!mentions || mentions.length === 0) return content

  let rendered = content
  const mentionRegex = /@\[([^\]]+)\]|@(\w+)/g

  rendered = rendered.replace(mentionRegex, (match) => {
    return `<span class="mention">${match}</span>`
  })

  return rendered
}

// =============================================
// QUERIES
// =============================================

/**
 * Fetch comments for a specific clip or shot
 * Uses batch fetching to avoid N+1 query problem
 */
export function useComments(commentableType: CommentableType, commentableId: string) {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: commentKeys.list(commentableType, commentableId),
    queryFn: async (): Promise<Comment[]> => {
      // Fetch comments with mentions in single query
      const { data, error } = await supabase
        .from('comments')
        .select(
          `
          *,
          comment_mentions (
            id,
            mentioned_user_id,
            created_at
          )
        `
        )
        .eq('commentable_type', commentableType)
        .eq('commentable_id', commentableId)
        .is('deleted_at', null)
        .order('created_at', { ascending: true })

      if (error) {
        console.error('[useComments] Error fetching comments:', error)
        throw error
      }

      if (!data || data.length === 0) {
        return []
      }

      // Batch fetch all user profiles in ONE query (fixes N+1)
      const userIds = [...new Set(
        data
          .filter(c => !c.is_system && c.user_id)
          .map(c => c.user_id)
      )]

      let userMap: Record<string, { id: string; name: string; email?: string; avatar_url?: string }> = {}

      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, email, full_name, avatar_url')
          .in('id', userIds)

        if (profiles) {
          userMap = Object.fromEntries(
            profiles.map(p => [p.id, {
              id: p.id,
              name: p.full_name || p.email || 'Unknown User',
              email: p.email,
              avatar_url: p.avatar_url,
            }])
          )
        }
      }

      // Map comments with cached user data
      const commentsWithUsers = data.map(comment => ({
        ...comment,
        user: comment.is_system
          ? { id: 'system', name: 'AionTrack', email: 'system@aiontrack.app' }
          : userMap[comment.user_id] || { id: comment.user_id, name: 'Unknown User' },
        mentions: comment.comment_mentions || [],
      })) as Comment[]

      return commentsWithUsers
    },
    enabled: !!user && !!commentableId,
    staleTime: 0,
    refetchOnMount: 'always',
  })

  // Set up real-time subscription
  useEffect(() => {
    if (!user || !commentableId) return

    const channel = supabase
      .channel(`comments:${commentableType}:${commentableId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'comments',
          filter: `commentable_type=eq.${commentableType},commentable_id=eq.${commentableId}`,
        },
        () => {
          // Invalidate and refetch comments on real-time update
          queryClient.invalidateQueries({
            queryKey: commentKeys.list(commentableType, commentableId),
          })
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user, commentableType, commentableId, queryClient])

  return query
}

/**
 * Fetch recent comments across all clips and shots in a project (activity feed)
 * Uses batch fetching to avoid N+1 query problem (was making 2-3 queries per comment!)
 */
export function useProjectCommentActivity(projectId: string, limit = 50) {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: commentKeys.projectActivity(projectId),
    queryFn: async (): Promise<CommentWithContext[]> => {
      // Fetch comments with mentions in single query
      const { data, error } = await supabase
        .from('comments')
        .select(
          `
          *,
          comment_mentions (
            id,
            mentioned_user_id,
            created_at
          )
        `
        )
        .eq('project_id', projectId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) {
        console.error('[useProjectCommentActivity] Error:', error)
        throw error
      }

      if (!data || data.length === 0) {
        return []
      }

      // Collect unique IDs for batch fetching
      const userIds = [...new Set(
        data.filter(c => !c.is_system && c.user_id).map(c => c.user_id)
      )]
      const clipIds = [...new Set(
        data.filter(c => c.commentable_type === 'clip').map(c => c.commentable_id)
      )]
      const shotIds = [...new Set(
        data.filter(c => c.commentable_type === 'shot').map(c => c.commentable_id)
      )]

      // Batch fetch all related data in parallel (3 queries instead of N*3)
      const [profilesResult, clipsResult, shotsResult] = await Promise.all([
        userIds.length > 0
          ? supabase.from('profiles').select('id, email, full_name, avatar_url').in('id', userIds)
          : { data: [] },
        clipIds.length > 0
          ? supabase.from('clips').select('id, name').in('id', clipIds)
          : { data: [] },
        shotIds.length > 0
          ? supabase.from('vfx_shots').select('id, shot_name').in('id', shotIds)
          : { data: [] },
      ])

      // Create lookup maps for O(1) access
      const userMap: Record<string, { id: string; name: string; email?: string; avatar_url?: string }> = {}
      if (profilesResult.data) {
        for (const p of profilesResult.data) {
          userMap[p.id] = {
            id: p.id,
            name: p.full_name || p.email || 'Unknown User',
            email: p.email,
            avatar_url: p.avatar_url,
          }
        }
      }

      const clipMap: Record<string, string> = {}
      if (clipsResult.data) {
        for (const c of clipsResult.data) {
          clipMap[c.id] = c.name
        }
      }

      const shotMap: Record<string, string> = {}
      if (shotsResult.data) {
        for (const s of shotsResult.data) {
          shotMap[s.id] = s.shot_name
        }
      }

      // Map comments with cached data (no additional queries!)
      const commentsWithContext = data.map(comment => {
        let commentableName = 'Unknown'
        let commentableUrl = '#'

        if (comment.commentable_type === 'clip' && clipMap[comment.commentable_id]) {
          commentableName = clipMap[comment.commentable_id]
          commentableUrl = `/projects/${projectId}/clips/${comment.commentable_id}`
        } else if (comment.commentable_type === 'shot' && shotMap[comment.commentable_id]) {
          commentableName = shotMap[comment.commentable_id]
          commentableUrl = `/projects/${projectId}/vfx-shots/${comment.commentable_id}`
        }

        return {
          ...comment,
          user: comment.is_system
            ? { id: 'system', name: 'AionTrack', email: 'system@aiontrack.app' }
            : userMap[comment.user_id] || { id: comment.user_id, name: 'Unknown User' },
          mentions: comment.comment_mentions || [],
          commentable_name: commentableName,
          commentable_url: commentableUrl,
        } as CommentWithContext
      })

      return commentsWithContext
    },
    enabled: !!user && !!projectId,
    staleTime: 30000, // 30 seconds
  })

  // Set up real-time subscription for project activity
  useEffect(() => {
    if (!user || !projectId) return

    const channel = supabase
      .channel(`comments:project:${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'comments',
          filter: `project_id=eq.${projectId}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: commentKeys.projectActivity(projectId),
          })
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user, projectId, queryClient])

  return query
}

// =============================================
// MUTATIONS
// =============================================

/**
 * Create a new comment
 */
export function useCreateComment() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (input: CreateCommentInput): Promise<Comment> => {
      if (!user) throw new Error('Not authenticated')

      // Insert comment
      const { data: comment, error: commentError } = await supabase
        .from('comments')
        .insert({
          content: input.content,
          user_id: user.id,
          project_id: input.project_id,
          commentable_type: input.commentable_type,
          commentable_id: input.commentable_id,
          is_system: false,
        })
        .select()
        .single()

      if (commentError) {
        console.error('[useCreateComment] Error creating comment:', commentError)
        throw commentError
      }

      // Insert mentions if any
      if (input.mentioned_user_ids && input.mentioned_user_ids.length > 0) {
        const mentions = input.mentioned_user_ids.map((userId) => ({
          comment_id: comment.id,
          mentioned_user_id: userId,
        }))

        const { error: mentionsError } = await supabase
          .from('comment_mentions')
          .insert(mentions)

        if (mentionsError) {
          console.error('[useCreateComment] Error creating mentions:', mentionsError)
          // Don't throw - comment was created successfully
        }
      }

      // Get user name from user_metadata if available
      const userAny = user as any
      const userMetadata = userAny.user_metadata as Record<string, any> | undefined

      return {
        ...comment,
        user: {
          id: user.id,
          name: userMetadata?.name || user.email || 'Unknown User',
          email: user.email,
        },
      } as Comment
    },
    onSuccess: (data) => {
      // Invalidate queries
      queryClient.invalidateQueries({
        queryKey: commentKeys.list(data.commentable_type, data.commentable_id),
      })
      queryClient.invalidateQueries({
        queryKey: commentKeys.projectActivity(data.project_id),
      })

      toast.success('Comment added')
    },
    onError: (error) => {
      console.error('[useCreateComment] Mutation error:', error)
      toast.error('Failed to add comment')
    },
  })
}

/**
 * Update a comment
 */
export function useUpdateComment(commentId: string) {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (input: UpdateCommentInput): Promise<Comment> => {
      if (!user) throw new Error('Not authenticated')

      // Update comment
      const { data: comment, error: commentError } = await supabase
        .from('comments')
        .update({
          content: input.content,
          updated_at: new Date().toISOString(),
        })
        .eq('id', commentId)
        .eq('user_id', user.id) // Ensure user owns the comment
        .select()
        .single()

      if (commentError) {
        console.error('[useUpdateComment] Error updating comment:', commentError)
        throw commentError
      }

      // Update mentions if provided
      if (input.mentioned_user_ids !== undefined) {
        // Delete existing mentions
        await supabase.from('comment_mentions').delete().eq('comment_id', commentId)

        // Insert new mentions
        if (input.mentioned_user_ids.length > 0) {
          const mentions = input.mentioned_user_ids.map((userId) => ({
            comment_id: commentId,
            mentioned_user_id: userId,
          }))

          await supabase.from('comment_mentions').insert(mentions)
        }
      }

      return comment as Comment
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: commentKeys.list(data.commentable_type, data.commentable_id),
      })
      queryClient.invalidateQueries({
        queryKey: commentKeys.projectActivity(data.project_id),
      })

      toast.success('Comment updated')
    },
    onError: (error) => {
      console.error('[useUpdateComment] Mutation error:', error)
      toast.error('Failed to update comment')
    },
  })
}

/**
 * Soft delete a comment
 */
export function useDeleteComment() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (commentId: string): Promise<Comment> => {
      if (!user) throw new Error('Not authenticated')

      const { data, error } = await supabase
        .from('comments')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', commentId)
        .eq('user_id', user.id) // Ensure user owns the comment
        .select()
        .single()

      if (error) {
        console.error('[useDeleteComment] Error:', error)
        throw error
      }

      return data as Comment
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: commentKeys.list(data.commentable_type, data.commentable_id),
      })
      queryClient.invalidateQueries({
        queryKey: commentKeys.projectActivity(data.project_id),
      })

      toast.success('Comment deleted')
    },
    onError: (error) => {
      console.error('[useDeleteComment] Mutation error:', error)
      toast.error('Failed to delete comment')
    },
  })
}
